import classNames from 'classnames';
import type { MouseEvent } from 'react';
import FJButton, { FJButtonType } from '../../common/components/FJButton/FJButton';
import FJThumbnailCover from '../../common/components/FJThumbnailCover/FJThumbnailCover';
import FJLoading from '../../common/components/FJLoading/FJLoading';
import FJMoreDropList from '../FJMoreDropList/FJMoreDropList';
import FJLocalStore from '../../store/FJLocalStore';
import { useGeneratingProgressLabels } from '../../hooks/useDisplayedProgressPercent';
import type { ProjectItem } from '../../types/FJProject';
import { GENERATE_STATE } from '../../constants/FJEnumConstant';
import styles from './FJRecentFileCard.module.less';
import FJLongToShortStore from '@src/store/FJLongToShortStore';
import FJToolTip from '@src/common/components/FJToolTip/FJToolTip';
import FJCheckBox from '../FJCheckBox/FJCheckBox';

interface FJRecentFileCardProps {
    item: ProjectItem;
    isSelected?: boolean;
    /** 是否处于多选模式（已有选中项时，点击卡片切换勾选而非跳转） */
    isMultiSelectMode?: boolean;
    onToggleSelected?: (id: string, shiftKey?: boolean) => void;
    onTryAgain?: (id: string) => void;
    onRename?: (id: string) => void;
    onDelete?: (id: string) => void;
    /** 点击卡片缩略图区域（`thumb_wrap`） */
    onCardClick?: (id: string) => void;
    /** pending 态点击「继续」：进入生成中页并做人脸识别 */
    onContinue?: (id: string) => void;
}

export default function FJRecentFileCard({
    item,
    isSelected = false,
    isMultiSelectMode = false,
    onToggleSelected,
    onTryAgain,
    onRename,
    onDelete,
    onCardClick,
    onContinue,
}: FJRecentFileCardProps) {
    const { generatingPercentLabel: genLabel, percentCompleteContinueLabel: continueLabel } =
        useGeneratingProgressLabels(item.progress ?? 0);

    const isThumbInteractive = Boolean(onCardClick) || isMultiSelectMode;

    const onThumbWrapClick = (e: MouseEvent<HTMLDivElement>) => {
        if (isMultiSelectMode) {
            onToggleSelected?.(item.id, e.shiftKey);
            return;
        }
        onCardClick?.(item.id);
    };

    const isHighlightExtractionFailure = FJLongToShortStore.isHighlightExtractionFailure(item.errorCode);
    return (
        <div className={styles.card}>
            <div
                className={classNames(styles.thumb_wrap, {
                    [styles.thumb_failed]: item.status === GENERATE_STATE.FAILED,
                    [styles.thumb_wrap_multi_select]: isMultiSelectMode,
                })}
                onClick={isThumbInteractive ? onThumbWrapClick : undefined}
            >
                <div className={styles.select_box} onClick={e => e.stopPropagation()}>
                    <FJCheckBox
                        selected={isSelected}
                        dataChange={(_, e) => {
                            e.stopPropagation();
                            onToggleSelected?.(item.id, e.shiftKey);
                        }}
                    />
                </div>
                {item.status !== GENERATE_STATE.FAILED && (
                    <FJThumbnailCover className={styles.thumb_cover} src={item.thumbnailUrl} alt='' />
                )}
                {item.status === GENERATE_STATE.PROCESSING && (
                    <div className={styles.loading_overlay}>
                        <FJLoading style={{ width: 18, height: 18 }} />
                        <span className={styles.label}>{genLabel}</span>
                    </div>
                )}
                {item.status === GENERATE_STATE.PENDING && (
                    <div className={styles.loading_overlay} style={{ cursor: 'default' }}>
                        <span className={styles.label}>{continueLabel}</span>
                        <FJButton
                            className={styles.click_btn}
                            textClassName={styles.click_btn_text}
                            type={FJButtonType.WHITE}
                            text='continue'
                            onClick={(e: MouseEvent<HTMLDivElement>) => {
                                e.stopPropagation();
                                if (isMultiSelectMode) {
                                    onToggleSelected?.(item.id, e.shiftKey);
                                    return;
                                }
                                onContinue?.(item.id);
                            }}
                        />
                    </div>
                )}
                {item.status === GENERATE_STATE.FAILED && (
                    <FJToolTip
                        title={
                            isHighlightExtractionFailure
                                ? FJLocalStore._('highlight-extraction-failed-quality-reason')
                                : ''
                        }
                    >
                        <div className={styles.overlay_failed} style={{ cursor: 'default' }}>
                            <p className={styles.label}>
                                {FJLocalStore._(
                                    isHighlightExtractionFailure
                                        ? 'highlight-extraction-failed-try-another-video'
                                        : 'generation-failed',
                                )}
                            </p>
                            {isHighlightExtractionFailure ? null : (
                                <FJButton
                                    className={styles.click_btn}
                                    textClassName={styles.click_btn_text}
                                    type={FJButtonType.WHITE}
                                    text='try-again'
                                    onClick={(e: MouseEvent<HTMLDivElement>) => {
                                        e.stopPropagation();
                                        if (isMultiSelectMode) {
                                            onToggleSelected?.(item.id, e.shiftKey);
                                            return;
                                        }
                                        onTryAgain?.(item.id);
                                    }}
                                />
                            )}
                        </div>
                    </FJToolTip>
                )}
            </div>

            <div className={styles.footer}>
                <div className={styles.meta}>
                    <div className={styles.file_title}>{item.title}</div>
                    <div className={styles.timestamp}>{item.timestamp}</div>
                </div>
                {!isMultiSelectMode && (
                    <FJMoreDropList
                        fileId={item.id}
                        onRename={onRename}
                        onDelete={onDelete}
                        className={styles.more_icon}
                    />
                )}
            </div>
        </div>
    );
}
